import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  text-align: center;
  background: var(--white);
  color: var(--text-black);
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  color: var(--error);
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  color: var(--text-gray);
  margin-bottom: 2rem;
  max-width: 600px;
`;

const ErrorDetails = styled.details`
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  max-width: 800px;
  text-align: left;

  summary {
    cursor: pointer;
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--text-black);
  }

  pre {
    font-size: 0.875rem;
    overflow-x: auto;
    color: var(--text-gray);
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const RetryButton = styled.button`
  padding: 0.75rem 2rem;
  background: var(--primary-blue);
  color: var(--white);
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background: var(--accent-blue);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(43, 155, 213, 0.3);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-blue);
    outline-offset: 2px;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Log to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to error reporting service (e.g., Sentry, LogRocket)
      console.error('Production error:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Reload the page to ensure a clean state
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorContainer role="alert">
          <ErrorTitle>Algo correu mal</ErrorTitle>
          <ErrorMessage>
            Ocorreu um erro inesperado. Por favor, tente novamente ou contacte o suporte se o
            problema persistir.
          </ErrorMessage>

          <RetryButton onClick={this.handleReset}>Recarregar Página</RetryButton>

          {this.state.error && (
            <ErrorDetails>
              <summary>Detalhes do Erro</summary>
              <pre>{this.state.error.toString()}</pre>
              {this.state.error.stack && (
                <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  {this.state.error.stack}
                </pre>
              )}
              {this.state.errorInfo && (
                <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
